import { type QueryClient } from '@tanstack/react-query'
import { createRootRouteWithContext, ErrorComponentProps, Outlet, useNavigate, useLocation } from '@tanstack/react-router'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import * as Sentry from '@sentry/react'
import { Toaster } from '@/components/ui/sonner'
import { NavigationProgress } from '@/components/navigation-progress'
import { GeneralError } from '@/features/errors/general-error'
import { NotFoundError } from '@/features/errors/not-found-error'
import { AuthProvider, useAuth } from '@/context/auth-context'
import { useEffect, useRef } from 'react'

function RouteGuard({ children }: { children: React.ReactNode }) {
  const { state } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const publicPaths = ['/sign-in', '/forgot-password', '/reset-password']
    const path = location.pathname
    if (!state.user && !publicPaths.includes(path)) {
      const redirect = location.href
      navigate({ to: '/sign-in', search: { redirect } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.user, location.pathname])

  return <>{children}</>
}

function RootRouteError({ error }: ErrorComponentProps) {
  const hasCaptured = useRef(false)

  useEffect(() => {
    if (hasCaptured.current) return

    hasCaptured.current = true
    Sentry.captureException(error)
  }, [error])

  return <GeneralError />
}

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient
}>()({
  component: () => {
    return (
      <AuthProvider>
        <RouteGuard>
          <NavigationProgress />
          <Outlet />
          <Toaster duration={5000} />
          {import.meta.env.MODE === 'development' && (
            <>
              <ReactQueryDevtools buttonPosition='bottom-left' />
              <TanStackRouterDevtools position='bottom-right' />
            </>
          )}
        </RouteGuard>
      </AuthProvider>
    )
  },
  notFoundComponent: NotFoundError,
  errorComponent: RootRouteError,
})
